import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { MessageCircle, Send, Star } from "lucide-react";

export default function BookingDetail() {
  const { session_id } = useParams();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [reviewed, setReviewed] = useState(false);
  const endRef = useRef(null);

  const refresh = () => {
    api.get(`/bookings/${session_id}`).then((r) => setBooking(r.data)).catch(() => {});
    api.get(`/bookings/${session_id}/messages`).then((r) => setMessages(r.data.items)).catch(() => {});
  };

  useEffect(() => {
    refresh();
    const interval = setInterval(() => {
      api.get(`/bookings/${session_id}/messages`).then((r) => setMessages(r.data.items)).catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [session_id]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post(`/bookings/${session_id}/messages`, { text });
      setText("");
      refresh();
    } catch (e) { toast.error(formatError(e)); }
  };

  const submitReview = async () => {
    if (!rating) return toast.error("Pick a star rating");
    try {
      await api.post(`/gurus/${booking.guru_id}/reviews`, {
        rating, text: reviewText, booking_session_id: session_id,
      });
      setReviewed(true);
      toast.success("Thank you for your review 🙏");
    } catch (e) { toast.error(formatError(e)); }
  };

  if (!user) return <div className="p-10 text-center text-[#63534E]">Please <Link to="/login" className="text-[#E65100] font-semibold">sign in</Link> to view this booking.</div>;
  if (!booking) return <div className="p-10 text-[#63534E]">Loading…</div>;

  return (
    <div data-testid="booking-detail" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="overline">Booking</div>
      <h1 className="font-serif text-4xl text-[#4A0E17] mt-2 leading-tight">{booking.service_title}</h1>
      <div className="text-[#63534E] mt-2">with <strong>{booking.guru_name}</strong> · ${booking.amount?.toFixed(2)} · <span className={`capitalize ${booking.payment_status === "paid" ? "text-green-700" : "text-amber-700"}`}>{booking.payment_status}</span></div>

      <div className="grid lg:grid-cols-3 gap-7 mt-10">
        <div className="lg:col-span-2 bg-[#FFFDF9] sacred-border rounded-3xl flex flex-col h-[500px]">
          <div className="px-6 py-4 border-b border-[#D4AF37]/30 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#E65100]" />
            <div className="font-serif text-xl text-[#4A0E17]">Chat with your teacher</div>
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-3" data-testid="chat-messages">
            {messages.length === 0 && <div className="text-sm text-[#63534E] text-center py-8">Say hello — the teacher will see your message before the session.</div>}
            {messages.map((m) => {
              const mine = m.from_user_id === user.id || m.from_role === (user.role === "guru" ? "teacher" : "devotee");
              return (
                <div key={m.id} data-testid={`msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? "bg-[#4A0E17] text-[#F3E5AB]" : "bg-[#F3E5AB]/50 text-[#1F1412]"}`}>
                    <div className={`text-[0.7rem] mb-0.5 ${mine ? "text-[#D4AF37]/80" : "text-[#8C6D3B]"}`}>{m.from_name} · {new Date(m.created_at).toLocaleTimeString()}</div>
                    <div className="whitespace-pre-wrap">{m.text}</div>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="border-t border-[#D4AF37]/30 p-4 flex gap-2">
            <Textarea data-testid="chat-input" rows={1} placeholder="Type a message… (Enter to send)" value={text}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
              onChange={(e) => setText(e.target.value)} className="bg-white border-[#D4AF37]/40 resize-none" />
            <Button data-testid="chat-send" type="submit" className="bg-[#E65100] hover:bg-[#BF360C] rounded-xl px-5"><Send className="w-4 h-4" /></Button>
          </form>
        </div>

        <aside className="bg-[#FFFDF9] sacred-border rounded-3xl p-6 h-fit">
          <div className="overline">Session Details</div>
          <div className="mt-3 text-sm space-y-2 text-[#1F1412]">
            <div>Booker: <strong>{booking.booker_name}</strong></div>
            <div>Email: <code className="text-xs">{booking.booker_email}</code></div>
            {booking.preferred_slot && <div>Preferred: {booking.preferred_slot}</div>}
            {booking.notes && <div className="text-[#63534E] italic mt-2">"{booking.notes}"</div>}
          </div>

          {booking.payment_status === "paid" && user.role !== "guru" && !reviewed && (
            <div className="mt-6 pt-6 border-t border-[#D4AF37]/30">
              <div className="overline">Rate your experience</div>
              <div className="flex gap-1 mt-3">
                {[1, 2, 3, 4, 5].map((r) => (
                  <button key={r} data-testid={`rate-${r}`} onClick={() => setRating(r)} className="transition-sacred">
                    <Star className={`w-7 h-7 ${rating >= r ? "fill-[#E65100] text-[#E65100]" : "text-[#D4AF37]/40"}`} />
                  </button>
                ))}
              </div>
              <Textarea data-testid="review-text" rows={3} placeholder="Share a few words (optional)" value={reviewText} onChange={(e) => setReviewText(e.target.value)} className="mt-3 bg-white border-[#D4AF37]/40" />
              <Button data-testid="submit-review" onClick={submitReview} disabled={!rating} className="mt-3 w-full bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-10 text-sm">Submit review</Button>
            </div>
          )}
          {reviewed && <div className="mt-5 bg-[#F3E5AB]/40 border border-[#D4AF37]/40 rounded-xl p-3 text-sm text-[#4A0E17] text-center">🙏 Thank you — your review is live</div>}
        </aside>
      </div>
    </div>
  );
}
